import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Check,
    HelpCircle,
    HardHat,
    Route as RouteIcon,
    Cable,
    Layers,
    Droplets,
    Ruler,
    Wrench,
} from "lucide-react";
import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationInternalLinks from "@/components/marketing/PrestationInternalLinks";
import ContactForm from "@/components/marketing/ContactForm";
import { SERVICES } from "@/lib/site";
import { buildBreadcrumb, buildService, buildFaqPage, SITE_ORIGIN } from "@/lib/schema";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const SLUG = "genie-civil-voirie-enrobes";

const PRESTATIONS = [
    {
        icon: <HardHat className="w-5 h-5" />,
        title: "Travaux de génie civil",
        text: "Nous intervenons sur des travaux de génie civil liés aux aménagements extérieurs, aux accès, aux plateformes, aux ouvrages techniques et aux infrastructures de terrain. Les prestations sont définies selon les besoins du chantier et les contraintes du site.",
        bullets: [
            "Petits ouvrages de génie civil",
            "Préparation d’infrastructures extérieures",
            "Plateformes techniques",
            "Bordures et caniveaux",
            "Travaux préparatoires avant revêtement",
        ],
    },
    {
        icon: <RouteIcon className="w-5 h-5" />,
        title: "Voirie, accès et parkings",
        text: "RCPS Terrassement réalise ou prépare des accès véhicules, chemins privés, cours, parkings, zones de circulation et surfaces carrossables. Ces travaux nécessitent une préparation correcte des fonds, une gestion des niveaux et une attention particulière à l’écoulement des eaux.",
        bullets: [
            "Accès de villas et immeubles",
            "Chemins privés",
            "Parkings et cours",
            "Zones de circulation",
            "Préparation de surfaces carrossables",
        ],
    },
    {
        icon: <Cable className="w-5 h-5" />,
        title: "Réseaux, tranchées et canalisations",
        text: "Dans le cadre de travaux de génie civil, nous pouvons intervenir sur la préparation de tranchées, réseaux enterrés, canalisations, chambres techniques et ouvrages associés, selon les plans disponibles et les intervenants concernés.",
        bullets: [
            "Tranchées techniques",
            "Préparation pour réseaux enterrés",
            "Canalisations selon projet",
            "Chambres et ouvrages associés",
            "Coordination avec les intervenants concernés",
        ],
    },
    {
        icon: <Layers className="w-5 h-5" />,
        title: "Enrobés et revêtements bitumineux",
        text: "Les enrobés permettent de créer des surfaces propres, fonctionnelles et adaptées aux accès véhicules, parkings, cours, chemins privés ou zones de circulation. La préparation du support est essentielle pour obtenir une surface adaptée à son usage.",
        bullets: [
            "Préparation avant enrobé",
            "Pose ou réfection d’enrobés selon projet",
            "Accès véhicules",
            "Parkings et cours",
            "Revêtements bitumineux extérieurs",
        ],
    },
];

const PROJECT_TYPES = [
    "Création d’accès véhicules",
    "Réfection d’accès existants",
    "Chemins privés",
    "Cours de villas",
    "Parkings privés ou professionnels",
    "Surfaces carrossables",
    "Tranchées techniques",
    "Réseaux enterrés",
    "Canalisations selon projet",
    "Bordures et caniveaux",
    "Préparation avant enrobé",
    "Pose ou réfection de revêtements bitumineux",
    "Plateformes extérieures",
    "Travaux pour régies, architectes, promoteurs, entreprises générales et collectivités",
];

const METHODE = [
    {
        title: "Analyse du projet",
        text: "Nous étudions l’usage prévu, la localisation, les accès, les niveaux, les réseaux connus, les surfaces concernées, les contraintes de circulation et les travaux nécessaires.",
        bullets: [
            "Usage de la surface",
            "Accès et circulation",
            "Niveaux et pentes",
            "Contraintes techniques",
        ],
    },
    {
        title: "Préparation du support",
        text: "La préparation du fond est une étape essentielle pour les accès, parkings, chemins, surfaces carrossables et enrobés. Elle dépend de la configuration du terrain et de l’usage prévu.",
        bullets: [
            "Décaissement si nécessaire",
            "Préparation des fonds",
            "Gestion des pentes",
            "Adaptation à l’usage prévu",
        ],
    },
    {
        title: "Travaux de voirie ou réseaux",
        text: "Selon le projet, nous réalisons les travaux prévus : tranchées, bordures, caniveaux, accès, plateformes, préparation de réseaux ou ouvrages associés.",
        bullets: [
            "Tranchées techniques",
            "Bordures et caniveaux",
            "Accès et parkings",
            "Coordination des travaux",
        ],
    },
    {
        title: "Enrobés et finitions extérieures",
        text: "Lorsque le projet le prévoit, la surface est préparée pour recevoir un revêtement adapté : enrobé, revêtement bitumineux ou autre finition extérieure selon l’usage et les contraintes du chantier.",
        bullets: [
            "Préparation avant enrobé",
            "Mise à niveau",
            "Finition de surface",
            "Remise en ordre de la zone",
        ],
    },
];

const VIGILANCE = [
    {
        icon: <Ruler className="w-5 h-5" />,
        title: "Préparation des fonds",
        text: "La qualité d’un accès, d’un parking ou d’un enrobé dépend fortement de la préparation du support, des niveaux et de la portance du terrain.",
    },
    {
        icon: <Droplets className="w-5 h-5" />,
        title: "Écoulement des eaux",
        text: "Les pentes, caniveaux, bordures et niveaux doivent être pensés pour limiter les stagnations d’eau et faciliter l’usage durable de la surface.",
    },
    {
        icon: <Wrench className="w-5 h-5" />,
        title: "Réseaux existants",
        text: "La présence de réseaux enterrés ou d’ouvrages existants doit être prise en compte avant toute intervention de tranchée, accès ou préparation de surface.",
    },
];

const POURQUOI = [
    {
        title: "Analyse du chantier",
        text: "Chaque demande est étudiée selon la localisation, les accès, les surfaces, l’usage prévu, les réseaux connus et les contraintes techniques.",
    },
    {
        title: "Préparation soignée",
        text: "Nous accordons une attention particulière aux fonds, aux niveaux, aux pentes et à la préparation avant revêtement.",
    },
    {
        title: "Travaux structurés",
        text: "Les étapes sont organisées afin de faciliter la réalisation des accès, parkings, tranchées, bordures, caniveaux ou enrobés.",
    },
    {
        title: "Coordination adaptée",
        text: "Lorsque plusieurs intervenants sont concernés, les travaux sont préparés afin de faciliter la coordination du chantier.",
    },
    {
        title: "Intervention locale",
        text: "Basés à Plan-les-Ouates, nous intervenons à Genève, dans le canton de Genève et dans toute la Suisse romande.",
    },
    {
        title: "Solutions adaptées",
        text: "Chaque projet reçoit une approche adaptée au terrain, à l’usage prévu, aux contraintes logistiques et aux besoins du client.",
    },
];

const FAQS = [
    {
        question: "Que couvre exactement la prestation génie civil, voirie & enrobés ?",
        answer: "Cette prestation regroupe les travaux liés aux accès, chemins privés, parkings, surfaces carrossables, bordures, caniveaux, tranchées, réseaux enterrés, préparation de supports et enrobés. Le contenu exact dépend du projet, du devis et des contraintes du chantier.",
    },
    {
        question: "Réalisez-vous les raccordements aux réseaux ?",
        answer: "RCPS Terrassement peut intervenir sur la préparation de tranchées, canalisations, réseaux enterrés et ouvrages associés selon le projet. Les raccordements effectifs peuvent nécessiter l’intervention ou la coordination avec les services, concessionnaires ou spécialistes concernés.",
    },
    {
        question: "Pouvez-vous créer un accès en enrobé pour une villa ?",
        answer: "Oui, nous pouvons étudier la création ou la réfection d’un accès véhicule, d’une cour ou d’un parking en enrobé. La faisabilité dépend de l’accès au chantier, de la surface, de la préparation du fond, des niveaux, de l’écoulement des eaux et de l’usage prévu.",
    },
    {
        question: "Combien de temps dure la pose d’un enrobé pour une cour ?",
        answer: "La durée dépend de la surface, de l’état du support, des travaux préparatoires, des accès, des niveaux, des bordures éventuelles et des conditions du chantier. Une analyse préalable permet d’évaluer l’organisation nécessaire.",
    },
    {
        question: "L’enrobé est-il adapté au climat suisse ?",
        answer: "L’enrobé est couramment utilisé pour les accès, parkings et surfaces carrossables. Sa durabilité dépend surtout de la préparation du support, de la gestion des eaux, de l’usage prévu et de la qualité de mise en œuvre.",
    },
    {
        question: "Intervenez-vous pour des projets publics ou de collectivités ?",
        answer: "Oui, RCPS Terrassement peut étudier des demandes pour des collectivités, communes, régies, entreprises générales, architectes, promoteurs, professionnels et particuliers à Genève et en Suisse romande.",
    },
];

export default function GenieCivilVoirieEnrobesPage() {
    const otherServices = SERVICES.filter((s) => s.slug !== SLUG);

    return (
        <>
            <Seo
                title="Génie civil Genève | Voirie, réseaux, accès et enrobés"
                description="RCPS Terrassement Sàrl réalise vos travaux de génie civil, voirie et enrobés à Genève et en Suisse romande : accès, parkings, réseaux enterrés, canalisations, bordures, chemins privés et revêtements bitumineux."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Prestations", path: "/prestations" },
                    { name: "Génie civil, voirie & enrobés", path: "/prestations/genie-civil-voirie-enrobes" },
                ])}
                id="breadcrumb-genie"
            />
            <JsonLd
                data={buildService({
                    slug: "genie-civil-voirie-enrobes",
                    name: "Génie civil, voirie et enrobés à Genève et en Suisse romande",
                    serviceType: "Génie civil, voirie, réseaux enterrés et enrobés",
                    description:
                        "Travaux de génie civil, voirie et enrobés pour particuliers et professionnels : accès, parkings, réseaux enterrés, canalisations, bordures, chemins privés et revêtements bitumineux à Genève et en Suisse romande.",
                    image: `${SITE_ORIGIN}/rcps-assets/prestation-genie-01.jpeg`,
                })}
                id="service-genie"
            />
            <JsonLd
                data={buildFaqPage(
                    `${SITE_ORIGIN}/prestations/genie-civil-voirie-enrobes`,
                    FAQS
                )}
                id="faq-genie"
            />

            {/* Hero */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="genie-hero"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/hhsqh0qq_Ge%CC%81nie%20civil%20et%20enrobe%CC%81s%20-%20Grande.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-900/55 via-navy-800/45 to-navy-700/70" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <Reveal>
                            <Breadcrumbs
                                variant="onImage"
                                trail={[
                                    { name: "Accueil", path: "/" },
                                    { name: "Prestations", path: "/prestations" },
                                    { name: "Génie civil, voirie & enrobés", path: "/prestations/genie-civil-voirie-enrobes" },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="kicker text-white/80 mt-5">
                                <span className="hairline bg-white/60" /> Prestation · Génie civil, voirie & enrobés
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="mt-5 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                                data-testid="genie-h1"
                            >
                                Génie civil, voirie et enrobés à Genève et en Suisse romande
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed">
                                RCPS Terrassement Sàrl intervient pour les travaux de génie civil,
                                voirie, accès, réseaux enterrés, parkings, bordures, caniveaux et
                                enrobés à Genève, dans l’ensemble du canton de Genève et dans toute la
                                Suisse romande.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-4 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                                Nous accompagnons les particuliers, architectes, régies, promoteurs,
                                entreprises générales, collectivités et professionnels pour préparer,
                                structurer et finaliser leurs infrastructures extérieures, selon la
                                configuration du site, les accès, les usages prévus et les contraintes
                                techniques du chantier.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Reveal delay={0.25}>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-devis"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-navy-700 font-semibold hover:bg-concrete-50 transition"
                            >
                                Demander un devis <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="bg-white" data-testid="genie-intro">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-6">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Génie civil à Genève
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Des travaux d’infrastructure adaptés à votre chantier.
                            </h2>
                        </Reveal>
                        <div className="mt-8 space-y-5 text-navy-700/85 text-base md:text-lg leading-[1.8]">
                            <Reveal delay={0.08}><p>Les travaux de génie civil et de voirie permettent de créer ou rénover les éléments essentiels d’un terrain : accès véhicules, chemins privés, parkings, réseaux enterrés, canalisations, bordures, caniveaux, plateformes, surfaces carrossables et revêtements extérieurs.</p></Reveal>
                            <Reveal delay={0.12}><p>À Genève comme en Suisse romande, ces travaux doivent être préparés avec méthode. La qualité d’un accès, d’un parking ou d’un enrobé dépend notamment de la préparation du support, de la gestion des niveaux, de l’écoulement des eaux, de la portance du terrain, des matériaux utilisés et des contraintes d’accès au chantier.</p></Reveal>
                            <Reveal delay={0.16}><p>RCPS Terrassement étudie chaque demande afin de proposer une intervention claire, réaliste et adaptée au projet.</p></Reveal>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[4/3]">
                                <img src="/rcps-assets/prestation-genie-01.jpeg" alt="Chantier de génie civil, voirie ou enrobés RCPS" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Prestations */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="genie-prestations"
            >
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-9">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Nos travaux de génie civil et voirie
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Des prestations pour structurer vos accès, réseaux et surfaces extérieures.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {PRESTATIONS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.05}>
                                <article
                                    className="h-full bg-white rounded-md border border-concrete-200 p-7 md:p-8"
                                    data-testid={`prestation-card-${i + 1}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center flex-shrink-0">
                                            {p.icon}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-mono-num text-xs text-steel">
                                                {String(i + 1).padStart(2, "0")}
                                            </p>
                                            <h3 className="mt-1 font-heading text-xl md:text-2xl font-semibold text-navy-700 tracking-tight">
                                                {p.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="mt-5 text-navy-700/80 leading-relaxed">{p.text}</p>
                                    <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                                        {p.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-navy-700 text-sm"
                                            >
                                                <span className="mt-1.5 w-3.5 h-3.5 rounded-full bg-concrete-100 flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                                </span>
                                                <span className="leading-relaxed">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Types de projets */}
            <section className="bg-white" data-testid="genie-project-types">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Pour quels projets ?
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Génie civil, voirie et enrobés pour particuliers, professionnels et collectivités.
                            </h2>
                            <p className="mt-6 text-navy-700/80 leading-relaxed">
                                RCPS Terrassement intervient sur des projets variés à Genève et en
                                Suisse romande. Chaque demande est étudiée selon la localisation du
                                chantier, les accès, l’usage prévu, la portance nécessaire, les
                                niveaux, les matériaux, les réseaux existants et les contraintes
                                logistiques.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="mb-5 aspect-[16/10] rounded-md overflow-hidden border border-concrete-200">
                                <img
                                    src="/rcps-assets/prestation-genie-02.jpeg"
                                    alt="Chantier de génie civil, voirie ou enrobés RCPS"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {PROJECT_TYPES.map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 bg-concrete-50 border border-concrete-200 rounded-md px-4 py-3 text-navy-700 text-sm md:text-base leading-snug"
                                    >
                                        <span className="mt-1 w-4 h-4 rounded-full bg-white border border-concrete-200 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                        </span>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Méthode */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="genie-methode"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Notre méthode
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                    Une intervention préparée pour des surfaces durables et fonctionnelles.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {METHODE.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`methode-step-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        Étape {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg md:text-xl font-semibold">
                                        {m.title}
                                    </h3>
                                    <p className="mt-3 text-white/75 text-sm leading-relaxed">
                                        {m.text}
                                    </p>
                                    <ul className="mt-4 space-y-1.5">
                                        {m.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-white/75 text-sm"
                                            >
                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                                                <span className="leading-snug">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Points de vigilance */}
            <InlineCTA
                variant="navy"
                title="Un projet de génie civil, voirie ou enrobés ?"
                text="Réseaux, structures de chaussée, enrobés, marquages : parlez-nous de votre chantier, nous revenons vers vous avec une proposition adaptée."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="genie-inline-cta"
            />
            <section className="bg-white" data-testid="genie-vigilance">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
                        <div className="md:col-span-5">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> À prendre en compte
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-[1.15]">
                                    Voirie, enrobés et réseaux : les points clés d’un chantier réussi.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7 space-y-5 text-navy-700/80 leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>
                                    Les travaux de génie civil, voirie et enrobés doivent prendre en
                                    compte plusieurs paramètres techniques : accès au chantier,
                                    portance du terrain, évacuation des eaux, pentes, usage futur de
                                    la surface, circulation, réseaux existants, matériaux, voisinage
                                    et contraintes communales éventuelles.
                                </p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>
                                    À Genève et en Suisse romande, certains travaux peuvent
                                    nécessiter une coordination avec des tiers, des informations
                                    techniques complémentaires ou des démarches spécifiques selon la
                                    commune, le site et l’ampleur des travaux. RCPS Terrassement
                                    analyse ces éléments pour proposer une intervention adaptée.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.16}>
                        <div className="mb-10 md:mb-12 aspect-[21/9] rounded-md overflow-hidden border border-concrete-200">
                            <img
                                src="/rcps-assets/prestation-genie-03.jpeg"
                                alt="Chantier RCPS — voirie, enrobés et réseaux"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VIGILANCE.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-concrete-200 bg-concrete-50 p-6 md:p-7"
                                    data-testid={`vigilance-card-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-md bg-white text-navy-700 border border-concrete-200 flex items-center justify-center">
                                        {v.icon}
                                    </div>
                                    <p className="mt-5 font-mono-num text-xs text-steel">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-1 font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-navy-700/75 text-sm md:text-base leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pourquoi RCPS */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="genie-pourquoi"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Pourquoi RCPS ?
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                                    Une approche structurée pour vos accès, réseaux et revêtements.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="mb-10 aspect-[21/9] rounded-md overflow-hidden border border-white/12">
                            <img
                                src="/rcps-assets/prestation-genie-04.jpeg"
                                alt="Génie civil, voirie et enrobés RCPS — surfaces structurées et fonctionnelles"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {POURQUOI.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`pourquoi-card-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg font-semibold">
                                        {h.title}
                                    </h3>
                                    <p className="mt-2 text-white/75 text-sm leading-relaxed">
                                        {h.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact / Formulaire */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="genie-contact"
            >
                <div className="rcps-container py-20 md:py-24 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Contact
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 leading-tight tracking-tight">
                                Parlons de votre projet de génie civil, voirie ou enrobés.
                            </h2>
                            <p className="mt-5 text-navy-700/80 leading-relaxed">
                                Vous souhaitez créer un accès, rénover une cour, préparer un parking,
                                réaliser des tranchées techniques, poser un enrobé ou structurer une
                                surface extérieure ? Transmettez-nous les informations principales
                                de votre chantier. RCPS Terrassement vous répondra après analyse de
                                votre demande.
                            </p>
                            <ul className="mt-6 space-y-2 text-navy-700/80 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Analyse selon la localisation et les accès</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Prise en compte de l’usage prévu et des réseaux</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Réponse après étude de votre demande</span>
                                </li>
                            </ul>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.08}>
                            <ContactForm defaultProjectType="Génie civil, voirie & enrobés" />
                            <p className="mt-4 text-xs md:text-sm text-navy-700/60 leading-relaxed">
                                Réponse après analyse de votre demande · devis personnalisé · sans
                                engagement.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="bg-white border-t border-concrete-200"
                data-testid="genie-faq"
            >
                <div className="rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                        <div className="md:col-span-5 md:sticky md:top-28">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Questions fréquentes
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-tight">
                                    Vos questions sur le génie civil, la voirie et les enrobés.
                                </h2>
                                <p className="mt-5 text-navy-700/80 leading-relaxed">
                                    Vous avez une question sur un accès, un parking, un réseau
                                    enterré, une cour ou un enrobé à Genève ou en Suisse romande ?
                                    Contactez-nous pour présenter votre chantier, sa localisation,
                                    son usage prévu et ses contraintes.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-navy-700">
                                    <span className="w-10 h-10 rounded-md bg-concrete-100 flex items-center justify-center">
                                        <HelpCircle className="w-5 h-5" />
                                    </span>
                                    <Link
                                        to="/contact"
                                        data-testid="faq-contact-link"
                                        className="text-sm font-semibold link-underline"
                                    >
                                        Poser une question à RCPS
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7">
                            <Reveal delay={0.08}>
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="w-full space-y-3"
                                    data-testid="genie-faq-accordion"
                                >
                                    {FAQS.map((f, i) => (
                                        <AccordionItem
                                            key={f.question}
                                            value={`item-${i}`}
                                            className="border border-concrete-200 rounded-md bg-white overflow-hidden [&[data-state=open]]:border-navy-700 transition"
                                        >
                                            <AccordionTrigger
                                                className="px-5 py-4 md:px-6 md:py-5 text-left hover:no-underline group"
                                                data-testid={`faq-question-${i}`}
                                            >
                                                <div className="flex items-start gap-4 pr-2">
                                                    <span className="font-mono-num text-xs text-navy-700/50 mt-1 flex-shrink-0">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-heading text-navy-700 font-semibold text-base md:text-lg leading-snug">
                                                        {f.question}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-5 md:px-6 pb-5 md:pb-6">
                                                <div className="pl-9 text-navy-700/80 text-sm md:text-base leading-[1.75]">
                                                    {f.answer}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <PrestationInternalLinks
                currentSlug={SLUG}
                relatedCategories={[
                    "Génie civil, voirie & enrobés",
                    "Terrassement",
                    "Aménagements extérieurs & enrochement",
                ]}
                bridgeText={
                    <>
                        <p>
                            Les travaux de génie civil, de voirie et d’enrobés reposent presque toujours
                            sur une préparation de terrain préalable et se prolongent souvent par une
                            phase d’aménagement.
                        </p>
                        <p>
                            Avant la pose d’un enrobé ou la création d’un accès, nous réalisons les{" "}
                            <Link
                                to="/prestations/terrassement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                travaux de terrassement à Genève et en Suisse romande
                            </Link>
                            {" "}: décaissement, préparation des fonds de forme et évacuation des matériaux.
                        </p>
                        <p>
                            Sur un site en réhabilitation, les{" "}
                            <Link
                                to="/prestations/demolition-deconstruction"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                travaux de démolition et déconstruction à Genève
                            </Link>
                            {" "}précèdent la reprise complète de la voirie. Et pour finaliser un
                            espace extérieur autour du bâti — bordures, talus, soutènements ou{" "}
                            <Link
                                to="/prestations/amenagements-exterieurs-enrochement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                aménagements extérieurs et enrochement en Suisse romande
                            </Link>
                            {" "}—, nous intervenons également.
                        </p>
                    </>
                }
            />

            {/* CTA final */}
            <SectionCTA
                title="Un projet de génie civil, voirie ou enrobés à concrétiser ?"
                text="Présentez-nous votre chantier. Nous étudions votre demande à Genève ou en Suisse romande selon la localisation, les accès, les surfaces, l’usage prévu, les réseaux connus, les niveaux et les contraintes techniques."
            />
        </>
    );
}
